import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { IException } from '../base/models'
import IPCheckbox from '../components/IPCheckBox'
import Modal from '../components/IPModal/IPModal'
import { IBanner } from '../pages/Homepage/commonModels/models'
import { GetPopupBannerApi } from '../pages/Homepage/services/getPopupBannerApi'
import { BannerDontShowAgainPopupApi } from '../pages/Homepage/services/dontShowAgainPopupApi'
import Image from '../components/IPImage/IPImage'
import { useAppDispatch, useAppSelector } from '../redux/app/hooks'
import { selectedTheme } from '../redux/slices/theme/themeSlice'
import { addOpenedPopupsCompanyIds, openedPopupsCompanyIdsRedux } from '../redux/slices/user/userSlice'
import { giniIntroFlightAtom } from '../components/ChatWidget/giniIntroFlight'
import {
  BANNER_TYPE_VIMEO,
  BANNER_TYPE_YOUTUBE,
  hasPopupContent,
  isMp4,
  vimeoEmbedUrl,
  youtubeEmbedUrl,
} from './bannerVideo'
import { consumePopupAfterRemote } from './popupAfterRemote'

// Video kapsayıcısı: modal `width='auto'` iken çökmemesi için genişlik açıkça verilir, oran 16:9.
const VIDEO_WIDTH = 'min(960px, 90vw)'
const videoFrameStyle: React.CSSProperties = { position: 'relative', width: VIDEO_WIDTH, paddingTop: '56.25%' }
const videoIframeStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  border: 0,
}
const IFRAME_ALLOW = 'autoplay; fullscreen; picture-in-picture; encrypted-media'

const withAnnouncementPopup =
  (WrappedComponent: any) =>
  ({ ...props }) => {
    const location = useLocation()
    const dispatch = useAppDispatch()
    const setGiniIntroFlight = useSetAtom(giniIntroFlightAtom)

    const [showAnnouncementPopup, setShowAnnouncementPopup] = useState(false)
    const [banner, setBanner] = useState<IBanner | undefined>()
    const [dontShowAgain, setDontShowAgain] = useState(false)
    const theme = useAppSelector(selectedTheme)
    const openedPopupCompanyIds = useAppSelector(openedPopupsCompanyIdsRedux)
    // Gini tanıtım popup'ı (video, görsel veya GIF) ekranda mı; kapanışta / sayfadan çıkışta uçuşu tetiklemek için
    const giniPlayingRef = useRef(false)
    // Remote geçişi reload ile gelir; login'deki `location.state` yerine tek kullanımlık işaret (mount'ta bir kez okunur)
    const [afterRemote] = useState(() => consumePopupAfterRemote())

    const isGiniIntro = !!banner?.IsGiniIntro
    const isBannerForTheme = (item: number) => (theme.id === 0 && !!banner?.IsHomepage) || theme.id === Number(item)
    const isPopupVisible = showAnnouncementPopup && !!banner && (banner.CompanyList || []).some(isBannerForTheme)

    useEffect(() => {
      const afterLogin =
        !!location?.state &&
        (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).type !== 'reload'
      if ((afterLogin || afterRemote) && openedPopupCompanyIds.indexOf(theme.id) === -1) {
        getAnnouncementBanner()
      }
    }, [theme])

    // Gini tanıtım popup'ı açıldığında ChatWidget baloncuklarını bekletir (tipten bağımsız)
    useEffect(() => {
      if (isPopupVisible && isGiniIntro && !giniPlayingRef.current) {
        giniPlayingRef.current = true
        setGiniIntroFlight('playing')
      }
    }, [isPopupVisible, isGiniIntro])

    // Popup kapanmadan sayfadan çıkılırsa ChatWidget 'playing'de takılı kalmasın
    useEffect(() => {
      return () => {
        if (giniPlayingRef.current) {
          giniPlayingRef.current = false
          setGiniIntroFlight('flying')
        }
      }
    }, [])

    const getAnnouncementBanner = () => {
      // TODO : company id
      const api: GetPopupBannerApi = new GetPopupBannerApi({})
      api
        .getBanner(theme.id)
        .then((res: IBanner) => {
          if (res && hasPopupContent(res)) {
            setBanner(res)
            setShowAnnouncementPopup(true)
            dispatch(addOpenedPopupsCompanyIds(theme.id))
          }
        })
        .catch((err: IException) => {})
    }

    const onPopupClose = () => {
      setShowAnnouncementPopup(false)

      if (giniPlayingRef.current) {
        giniPlayingRef.current = false
        setGiniIntroFlight('flying')
      }

      // Gini tanıtım popup'ı için "Bir daha gösterme" kullanılamaz
      if (banner && dontShowAgain && !banner.IsGiniIntro) {
        const dontShowAgainPopupApi: BannerDontShowAgainPopupApi = new BannerDontShowAgainPopupApi({})
        dontShowAgainPopupApi.dontShowAgain(banner.Id)
      }
    }

    const renderBannerImage = (b: IBanner) => (
      <Image className='banner' style={{ height: '100%', padding: '5px 0 0 0' }} src={b.ImageUrl} alt={b.Title} />
    )

    const renderContent = (b: IBanner) => {
      if (b.BannerType === BANNER_TYPE_YOUTUBE || b.BannerType === BANNER_TYPE_VIMEO) {
        const src = b.BannerType === BANNER_TYPE_YOUTUBE ? youtubeEmbedUrl(b.VideoId) : vimeoEmbedUrl(b.VideoId)
        if (!src) return null
        return (
          <div style={videoFrameStyle}>
            <iframe
              src={src}
              title={b.BannerType === BANNER_TYPE_YOUTUBE ? 'YouTube video player' : 'Vimeo video player'}
              style={videoIframeStyle}
              allow={IFRAME_ALLOW}
              allowFullScreen
              referrerPolicy='strict-origin-when-cross-origin'
            ></iframe>
          </div>
        )
      }
      if (b.BannerType === 1) return renderBannerImage(b)
      // Yalnız mp4 Gini popup'ı video bitince kendini kapatır; görsel/GIF'te otomatik kapanma yok
      if (isMp4(b.Image)) {
        return (
          <video
            autoPlay
            muted
            playsInline
            controls
            preload='auto'
            onEnded={b.IsGiniIntro ? onPopupClose : undefined}
            style={{ width: VIDEO_WIDTH, maxHeight: '80vh', padding: '5px 0 0 0' }}
          >
            <source src={b.ImageUrl} type='video/mp4'></source>
            Tarayıcınız video oynatmayı desteklemiyor.
          </video>
        )
      }
      if (b.ProductAddress) {
        return (
          <a href={b.ProductAddress} target={b.BannerType === 6 ? '_blank' : ''}>
            {renderBannerImage(b)}
          </a>
        )
      }
      return (
        <Link to={`/products?bannerId=${b.Id}`} target={b.BannerType === 6 ? '_blank' : ''}>
          {renderBannerImage(b)}
        </Link>
      )
    }

    return (
      <>
        <WrappedComponent {...props} />
        {banner?.CompanyList.map(
          item =>
            isBannerForTheme(item) && (
              <Modal
                title={banner?.Title}
                width={'auto'}
                show={showAnnouncementPopup}
                onClose={onPopupClose}
                hideConfirm={true}
                checkbox={
                  isGiniIntro ? undefined : (
                    <IPCheckbox
                      checked={dontShowAgain}
                      label={'Bir daha gösterme'}
                      onChange={e => setDontShowAgain(e.target.checked)}
                      style={{ float: 'left' }}
                    />
                  )
                }
              >
                <div className='p-3'>
                  <div className='row'>{renderContent(banner)}</div>
                </div>
              </Modal>
            )
        )}
      </>
    )
  }

export { withAnnouncementPopup }
